import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import api from "../../../../Component/axios_Interceptor/api";
import api2 from "../../../../Component/axios_Interceptor/api2";
import url, { SERVER_ADDRESS } from "../../../../Component/server";
import LiveDetailDialog from "./LiveDetailDialog";
import ShippingPolicyFields, { ShippingMode } from "./ShippingPolicyFields";
import ChatMuteControls from "../../UserList/ChatMuteControls";
import ChatModerationDialog from './ChatModerationDialog';
import { ChatRestriction, publishLiveBan } from '../../../Chat/useChatRestrictions';

const LiveSideActions = ({ nickname: selectedNickname, compact = false, restriction: cachedRestriction }: { nickname?: string; compact?: boolean; restriction?: ChatRestriction | null }) => {
  const [banNickname, setBanNickname] = useState("");
  const [banMessage, setBanMessage] = useState("");
  const [banError, setBanError] = useState("");
  const [banSaving, setBanSaving] = useState(false);
  const banPending = useRef(false);
  const [banConfirmation,setBanConfirmation] = useState<{ nickname: string; release: boolean } | null>(null);
  const [restriction, setRestriction] = useState<{ nickname: string; blocked: boolean; muted: boolean } | null>(null);
  const [restrictionError, setRestrictionError] = useState('');
  const selectedReady = selectedNickname === undefined || (cachedRestriction !== undefined ? cachedRestriction !== null : restriction?.nickname === selectedNickname);
  const currentRestriction = cachedRestriction === undefined ? restriction : cachedRestriction;

  useEffect(() => {
    if (selectedNickname === undefined || cachedRestriction !== undefined) return;
    let active = true;
    setRestriction(null); setRestrictionError('');
    const contains = async (endpoint: string) => {
      for (let page = 1; active; page++) {
        const { data } = await api.get(endpoint, { params: { current_page: page, per_page: 100, search_key: 'nickname', search_value: selectedNickname }, timeout: 10000 });
        if (!active) return false;
        if (!Array.isArray(data?.userList) || !Number.isSafeInteger(data.total) || data.total < 0) throw new Error('Invalid restriction list');
        if (data.userList.some((member: { nickname: string }) => member.nickname === selectedNickname)) return true;
        if (page * 100 >= data.total) return false;
        if (!data.userList.length) throw new Error('Incomplete restriction list');
      }
      return false;
    };
    void Promise.all([contains(url.Communication_url.ban_user_list), contains(`${SERVER_ADDRESS}relation/chat-muted-user-list`)])
      .then(([blocked, muted]) => { if (active) setRestriction({ nickname: selectedNickname, blocked, muted }); })
      .catch(() => { if (active) setRestrictionError('차단·금지 상태를 확인하지 못했습니다. 메뉴를 다시 열어주세요.'); });
    return () => { active = false; };
  }, [selectedNickname, cachedRestriction]);

  const banUser = async () => {
    const nickname = banConfirmation?.nickname;
    const release = banConfirmation?.release;
    if (banPending.current || !nickname || !selectedReady) return;
    banPending.current = true; setBanSaving(true);
    setBanError(""); setBanMessage("");
    try {
      const { data } = release ? await api.delete(url.Communication_url.user_unban, { data: { nickname } })
        : await api.post(url.Communication_url.user_ban, { nickname });
      if (data !== "성공") throw new Error("차단 요청을 처리하지 못했습니다.");
      setBanNickname("");
      setBanMessage(release ? `${nickname} 님의 라이브 차단을 해제했습니다.` : `${nickname} 님을 차단했습니다.`);
      publishLiveBan(nickname, !release);
      if (selectedNickname !== undefined) setRestriction(current => current && { ...current, blocked: !release });
      setBanConfirmation(null);
    } catch (failure: any) {
      setBanError(failure?.response?.data?.message || (release ? '라이브 차단을 해제하지 못했습니다.' : '사용자를 차단하지 못했습니다.'));
    } finally { banPending.current = false; setBanSaving(false); }
  };

  return <SideActions $compact={compact} data-selected-user={selectedNickname !== undefined || undefined}>
    <BanRow>
      {selectedNickname === undefined && <input aria-label="차단할 사용자 닉네임" value={banNickname} disabled={banSaving} onChange={event => setBanNickname(event.target.value)}
        placeholder="차단할 닉네임을 입력해주세요." />}
      <button type="button" data-chat-action="block" disabled={banSaving || !selectedReady} onClick={() => {
        const nickname=(selectedNickname ?? banNickname).trim();
        if (!nickname || banPending.current) return;
        setBanError('');setBanConfirmation({ nickname, release: selectedNickname !== undefined && Boolean(currentRestriction?.blocked) });
      }}>{banSaving ? '처리 중…' : currentRestriction?.blocked ? '라이브 차단 해제' : '라이브 차단'}</button>
    </BanRow>
    {selectedNickname === undefined && banMessage && <Feedback role="status">{banMessage}</Feedback>}
    {banError && !banConfirmation && <Feedback role="alert">{banError}</Feedback>}
    <ChatMuteControls nickname={selectedNickname} confirmInDialog disabled={!selectedReady} muted={Boolean(currentRestriction?.muted)}
      onMutedChange={muted => setRestriction(current => current && { ...current, muted })} />
    {selectedNickname !== undefined && !selectedReady && <Feedback role={restrictionError ? 'alert' : 'status'}>{restrictionError || '차단·금지 상태 확인 중…'}</Feedback>}
    {banConfirmation && <ChatModerationDialog action={banConfirmation.release ? 'unblock' : 'block'} nickname={banConfirmation.nickname} saving={banSaving} error={banError}
      onConfirm={() => void banUser()} onCancel={() => {if(!banPending.current)setBanConfirmation(null);}} />}
  </SideActions>;
};

export const LiveShippingAction = ({ disabled = false }: { disabled?: boolean }) => {
  const { seq } = useParams();
  const [shippingOpen, setShippingOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [mode, setMode] = useState<ShippingMode>("threshold");
  const [basePrice, setBasePrice] = useState(4000);
  const [freeThreshold, setFreeThreshold] = useState(50000);
  const [jejuPrice, setJejuPrice] = useState(7000);
  const [remotePrice, setRemotePrice] = useState(7000);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const actionsDisabled = disabled || !['예약', '리허설', '방송중'].includes(status);

  useEffect(() => {
    if (actionsDisabled) setShippingOpen(false);
  }, [actionsDisabled]);

  useEffect(() => {
    let active = true;
    setStatus('');
    api2.get(url.Communication_url.get_live_info, { params: { seq } }).then(({ data }) => {
      if (!active) return;
      setStatus(data.live_status || "");
      setMode(data.shipping_mode === "free" ? "free" : data.shipping_mode === "fixed" ? "fixed" : "threshold");
      setBasePrice(Number(data.base_delivery_price ?? 4000));
      setFreeThreshold(Number(data.free_shipping_threshold ?? 50000));
      setJejuPrice(Number(data.jeju_delivery_price ?? 7000));
      setRemotePrice(Number(data.remote_delivery_price ?? 7000));
    }).catch(() => { if (active) setError("배송 정보를 불러오지 못했습니다."); });
    return () => { active = false; };
  }, [seq]);

  const saveShipping = async () => {
    if (actionsDisabled || saving || !seq || status !== "예약") return;
    if (mode !== "free" && (basePrice <= 0 || (mode === "threshold" && freeThreshold <= 0) || jejuPrice <= 0 || remotePrice <= 0)) {
      setError("무료배송 기준과 지역별 배송비를 모두 입력해주세요.");
      return;
    }
    setSaving(true); setError(""); setMessage("");
    try {
      const { data } = await api.patch(`${SERVER_ADDRESS}live/shipping-policy`, {
        seq: Number(seq), shipping_mode: mode,
        base_delivery_price: mode === "free" ? 0 : basePrice,
        free_shipping_threshold: mode === "fixed" ? null : mode === "free" ? 0 : freeThreshold,
        jeju_delivery_price: mode === "free" ? 0 : jejuPrice,
        remote_delivery_price: mode === "free" ? 0 : remotePrice,
      });
      setMode(data.shipping_mode);
      setBasePrice(Number(data.base_delivery_price));
      setFreeThreshold(Number(data.free_shipping_threshold ?? 0));
      setJejuPrice(Number(data.jeju_delivery_price ?? 0));
      setRemotePrice(Number(data.remote_delivery_price ?? 0));
      setMessage("배송 정보가 저장되었습니다.");
      setShippingOpen(false);
    } catch (failure: any) {
      setError(failure?.response?.data?.message || "배송 정보를 저장하지 못했습니다.");
    } finally { setSaving(false); }
  };

  return <ShippingAction aria-label="배송 정보 관리">
    <ShippingButton type="button" disabled={actionsDisabled} onClick={() => { setMessage(""); setError(""); setShippingOpen(true); }}>배송 정보 수정</ShippingButton>
    {message && <Feedback role="status">{message}</Feedback>}
    {!shippingOpen && error && <Feedback role="alert">{error}</Feedback>}
    <LiveDetailDialog open={shippingOpen && !actionsDisabled} onClose={() => setShippingOpen(false)} title="배송 정보 수정"
      description="결제 전에 고객에게 적용될 배송 정보를 수정합니다.">
      <ShippingForm>
        <ShippingPolicyFields mode={mode} baseDeliveryPrice={basePrice} freeShippingThreshold={freeThreshold}
          jejuDeliveryPrice={jejuPrice} remoteDeliveryPrice={remotePrice} disabled={status !== "예약" || saving}
          onModeChange={setMode} onBaseDeliveryPriceChange={setBasePrice}
          onFreeShippingThresholdChange={setFreeThreshold} onJejuDeliveryPriceChange={setJejuPrice}
          onRemoteDeliveryPriceChange={setRemotePrice} />
        {status !== "예약" && <Feedback>예약 상태의 방송만 배송 정보를 수정할 수 있습니다.</Feedback>}
        {error && <Feedback role="alert">{error}</Feedback>}
        <button type="button" disabled={status !== "예약" || saving} onClick={saveShipping}>
          {saving ? "저장 중…" : "배송 정보 저장"}
        </button>
      </ShippingForm>
    </LiveDetailDialog>
  </ShippingAction>;
};

export default LiveSideActions;

const SideActions = styled.div<{ $compact: boolean }>`
  display: flex; flex-direction: column; gap: 12px;
  &[data-selected-user] button { width: 100%; min-height: var(--control-lg); }
  ${({ $compact }) => $compact && `
    display: grid; grid-template-columns: repeat(2, max-content); gap: var(--space-1); align-items: start;
    &[data-selected-user] button { width: auto; min-width: 48px; min-height: var(--control-sm); padding: 0 var(--space-2); font: inherit; }
    > p { grid-column: 1 / -1; max-width: 240px; font-size: var(--text-caption); }
    section p { max-width: 140px; }
  `}
`;
const ShippingAction = styled.section`
  box-sizing: border-box; min-width: 0; padding: 12px; border: 1px solid var(--admin-theme-border, #e9e6ef);
  border-radius: 12px; background: var(--admin-theme-surface, #fff);
`;
const BanRow = styled.div`
  display: flex; gap: 8px;
  input { min-width: 0; flex: 1; min-height: 42px; padding: 0 12px; border: 1px solid var(--admin-theme-border, #f0f0f0); border-radius: 8px; font: inherit; }
  button { min-width: 54px; border: 0; border-radius: 7px; color: #fff; background: var(--admin-solid, #292433); cursor: pointer; }
`;
const ShippingButton = styled.button`
  width: 100%; min-height: 44px; border: 1px solid var(--admin-theme-border, #d8d0e8); border-radius: 8px;
  background: var(--admin-theme-accent-soft, #f7f4ff); color: var(--admin-theme-link, #5730c4); font-size: 14px; font-weight: 600; cursor: pointer;
  &:hover:not(:disabled) { border-color: #8464dd; background: var(--admin-theme-accent-soft, #f0eaff); }
  &:disabled { opacity: .55; cursor: not-allowed; }
  &:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
`;
const ShippingForm = styled.div`
  > button { width: 100%; min-height: 48px; margin-top: 16px; border: 0; border-radius: 8px; background: #6338dc; color: white; cursor: pointer; }
  > button:disabled { background: var(--admin-theme-raised, #ddd); color: var(--admin-theme-muted, #888); cursor: default; }
`;
const Feedback = styled.p`margin: 0; color: var(--admin-theme-success, #246b36); font-size: 13px; &[role="alert"] { color: var(--admin-theme-danger, #bd0030); }`;
